import React from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  Switch,
  IconButton,
  CircularProgress,
  Collapse,
  TextField,
  Grid,
  Alert,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  Code as CodeIcon,
  BugReport as BugReportIcon,
  PlayCircle as PlayCircleIcon,
  Search as SearchIcon,
  FolderOpen as FolderOpenIcon,
  SmartToy as SmartToyIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import SettingsRowShell from './SettingsRowShell';

const TOOLS = [
  { key: 'portal',     label: 'QA Portal Tools',           desc: 'Search test cases, modules, step catalog',       icon: <StorageIcon fontSize="small" />,    locked: true },
  { key: 'github',     label: 'GitHub Repository',         desc: 'Read/write files, list repo structure',           icon: <CodeIcon fontSize="small" />,       req: 'GitHub OAuth' },
  { key: 'jira',       label: 'Jira / Atlassian',          desc: 'Search issues, create bugs, transition tickets',  icon: <BugReportIcon fontSize="small" />,  req: 'Jira OAuth' },
  { key: 'mysql',      label: 'MySQL Database',            desc: 'Query tables, describe schemas, run SQL',         icon: <StorageIcon fontSize="small" />,    req: 'Connection config', expandable: true },
  { key: 'playwright', label: 'Playwright Browser',        desc: 'Navigate, click, fill forms, take screenshots',   icon: <PlayCircleIcon fontSize="small" /> },
  { key: 'web_search', label: 'Web Search & Fetch',        desc: 'Search DuckDuckGo, fetch web pages',              icon: <SearchIcon fontSize="small" /> },
  { key: 'filesystem', label: 'Filesystem / Code Editing', desc: 'Read, write, search workspace files',             icon: <FolderOpenIcon fontSize="small" /> },
];

const AiToolsRow = ({
  toolToggles,
  setToolToggles,
  copilotSettings,
  loadingCopilot,
  savingCopilot,
  onSave,
  onRefresh,
  /* MySQL props */
  mysqlForm,
  setMysqlForm,
  testingMysql,
  onTestMysql,
  onRemoveMysql,
}) => {
  const enabledCount = Object.values(toolToggles).filter(Boolean).length;

  const getReqChip = (tool) => {
    if (!tool.req) return null;
    const connectedMap = {
      jira: copilotSettings?.jira_connected,
      github: copilotSettings?.github_connected,
      mysql: copilotSettings?.mysql_configured,
    };
    const connected = connectedMap[tool.key];
    if (connected === undefined) return null;
    return connected
      ? <Chip label="Connected" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
      : <Chip label={`Requires ${tool.req}`} size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />;
  };

  const mysqlConfigured = copilotSettings?.mysql_configured;
  const mysqlConn = copilotSettings?.mysql_connection;

  return (
    <SettingsRowShell
      icon={<SmartToyIcon />}
      title="QAssure AI Tools"
      description="Configure which capabilities are available to QAssure AI"
      status={{ label: `${enabledCount}/${TOOLS.length} Enabled`, color: 'info' }}
      loading={loadingCopilot}
      actions={
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={savingCopilot ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={onSave}
            disabled={savingCopilot || loadingCopilot}
            sx={{ textTransform: 'none', fontSize: '0.8rem' }}
          >
            {savingCopilot ? 'Saving…' : 'Save'}
          </Button>
          <IconButton onClick={onRefresh} disabled={loadingCopilot} size="small">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      }
    >
      {loadingCopilot ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {TOOLS.map((tool) => (
            <React.Fragment key={tool.key}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  opacity: toolToggles[tool.key] ? 1 : 0.55,
                  borderColor: toolToggles[tool.key] ? 'primary.main' : 'divider',
                  transition: 'all 0.2s',
                  borderRadius: 1.5,
                }}
              >
                <Box sx={{ color: toolToggles[tool.key] ? 'primary.main' : 'text.disabled' }}>
                  {tool.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>{tool.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{tool.desc}</Typography>
                  {getReqChip(tool) && <Box sx={{ mt: 0.5 }}>{getReqChip(tool)}</Box>}
                </Box>
                <Switch
                  checked={toolToggles[tool.key] || false}
                  onChange={(e) =>
                    tool.locked
                      ? null
                      : setToolToggles((prev) => ({ ...prev, [tool.key]: e.target.checked }))
                  }
                  disabled={tool.locked}
                  size="small"
                  color="primary"
                />
              </Paper>

              {/* ---- Inline MySQL config when mysql toggle is on ---- */}
              {tool.key === 'mysql' && (
                <Collapse in={toolToggles.mysql} timeout="auto" unmountOnExit>
                  <Paper
                    variant="outlined"
                    sx={{
                      ml: 4,
                      p: 2,
                      borderColor: 'primary.light',
                      borderRadius: 1.5,
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                      MySQL Connection
                    </Typography>

                    {mysqlConfigured && (
                      <Alert severity="success" sx={{ mb: 1.5, py: 0 }}>
                        Connected:&nbsp;
                        <strong>{mysqlConn?.username}@{mysqlConn?.host}:{mysqlConn?.port}/{mysqlConn?.database}</strong>
                      </Alert>
                    )}

                    <Grid container spacing={1.5}>
                      <Grid item xs={8}>
                        <TextField
                          label="Host"
                          value={mysqlForm.host}
                          onChange={(e) => setMysqlForm((p) => ({ ...p, host: e.target.value }))}
                          fullWidth size="small" placeholder="localhost or db.example.com"
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          label="Port" type="number"
                          value={mysqlForm.port}
                          onChange={(e) => setMysqlForm((p) => ({ ...p, port: e.target.value }))}
                          fullWidth size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Database"
                          value={mysqlForm.database}
                          onChange={(e) => setMysqlForm((p) => ({ ...p, database: e.target.value }))}
                          fullWidth size="small" placeholder="my_database"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Username"
                          value={mysqlForm.username}
                          onChange={(e) => setMysqlForm((p) => ({ ...p, username: e.target.value }))}
                          fullWidth size="small"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Password" type="password"
                          value={mysqlForm.password}
                          onChange={(e) => setMysqlForm((p) => ({ ...p, password: e.target.value }))}
                          fullWidth size="small"
                          placeholder={mysqlConfigured ? '••••••• (saved)' : ''}
                        />
                      </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                      <Button
                        variant="outlined" size="small"
                        onClick={onTestMysql}
                        disabled={testingMysql || !mysqlForm.host || !mysqlForm.password}
                        startIcon={testingMysql ? <CircularProgress size={14} /> : <PlayCircleIcon />}
                        sx={{ textTransform: 'none' }}
                      >
                        {testingMysql ? 'Testing…' : 'Test Connection'}
                      </Button>
                      {mysqlConfigured && (
                        <Button
                          variant="outlined" color="error" size="small"
                          onClick={onRemoveMysql}
                          startIcon={<DeleteIcon />}
                          sx={{ textTransform: 'none' }}
                        >
                          Remove
                        </Button>
                      )}
                    </Box>

                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="caption" color="text.secondary">
                      The MySQL password is encrypted at rest. Click <strong>Save</strong> above to persist changes.
                    </Typography>
                  </Paper>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </Box>
      )}
    </SettingsRowShell>
  );
};

export default AiToolsRow;
